import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	HeatAgeThreshold,
	HeatAgeThresholdDraft,
} from "@/shared/models/HeatAgeThreshold";
import { upsertHeatAgeThresholds } from "@/shared/repositories/HeatAgeThresholdsRepository";
import { ErrorModal } from "@dexteel/mesf-core";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useHeatAgeThresholds } from "@/shared/components/TimeSinceLastBillet";
import { HeatAgeThresholdBar } from "./HeatAgeThresholdBar";

const toDrafts = (thresholds: HeatAgeThreshold[]): HeatAgeThresholdDraft[] =>
	thresholds
		.map(({ Level, Days }) => ({ Level, Days }))
		.sort((a, b) => a.Days - b.Days);

const moveLevel = (
	levels: HeatAgeThresholdDraft[],
	index: number,
	days: number,
): HeatAgeThresholdDraft[] => {
	const next = levels.map((level, i) =>
		i === index ? { ...level, Days: days } : level,
	);

	for (let i = index + 1; i < next.length; i++)
		next[i] = {
			...next[i],
			Days: Math.max(next[i].Days, next[i - 1].Days + 1),
		};

	for (let i = index - 1; i >= 0; i--)
		next[i] = {
			...next[i],
			Days: Math.min(next[i].Days, next[i + 1].Days - 1),
		};

	return next;
};

export const HeatAgeThresholdsForm = () => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [levels, setLevels] = useState<HeatAgeThresholdDraft[]>([]);

	const { data: saved, isLoading } = useHeatAgeThresholds();

	const savedLevels = toDrafts(saved ?? []);

	const saveMutation = useMutation(upsertHeatAgeThresholds, {
		meta: { successMessage: "Thresholds saved" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["heatAgeThresholds"],
			});
		},
		onError: (e: Error) => setError(e.message),
	});

	const isDirty = !(
		levels.length === savedLevels.length &&
		levels.every((level, index) => level.Days === savedLevels[index].Days)
	);
	const isBusy = saveMutation.isPending;

	useEffect(() => {
		if (saved) {
			setLevels(toDrafts(saved));
		}
	}, [saved]);

	if (isLoading)
		return (
			<Box sx={{ py: 4 }}>
				<CenteredLazyLoading />
			</Box>
		);

	return (
		<>
			<Paper elevation={2} sx={{ p: 2 }}>
				<Stack spacing={2}>
					<Box>
						<Typography variant="h6" fontWeight={600}>
							Heat Age Thresholds
						</Typography>
						<Typography variant="body2" color="text.secondary">
							How long a heat may wait for its lab result before the Since Last
							Billet column flags it. Drag a handle to move where a level
							starts; the bar is painted with the very colours the column will
							use.
						</Typography>
					</Box>

					<HeatAgeThresholdBar
						levels={levels}
						maxDays={Math.max(
							10,
							(savedLevels[savedLevels.length - 1]?.Days ?? 0) + 2,
						)}
						disabled={isBusy}
						onChangeDays={(index, days) =>
							setLevels((current) => moveLevel(current, index, days))
						}
					/>

					<Stack direction="row" spacing={1} justifyContent="flex-end">
						<Button
							onClick={() => setLevels(savedLevels)}
							disabled={isBusy || !isDirty}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={() => saveMutation.mutate(levels)}
							disabled={isBusy || !isDirty}
						>
							Save
						</Button>
					</Stack>
				</Stack>
			</Paper>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
